import { useState } from 'react';
import { Plus } from 'lucide-react';
import { useSubtasks } from '../../../hooks/useTaskDetail';
import { useCreateTask } from '../../../hooks/useTasks';
import { StatusIcon } from './taskVisuals';

/**
 * CLE-182 — dark-theme sub-issues section for the task detail page.
 *
 * Shows the child issues with a progress count + bar, an inline quick-add
 * ("+ Add sub-issues"), and rows that navigate to each child's detail page.
 */
export function SubIssues({
  taskId,
  parentProjectId,
  onOpenTask,
}: {
  taskId: string;
  // A sub-issue inherits its parent's project (CLE-167: every task has one).
  parentProjectId: string;
  onOpenTask: (id: string) => void;
}) {
  const { data, isLoading } = useSubtasks(taskId);
  const create = useCreateTask();
  const [title, setTitle] = useState('');
  const [adding, setAdding] = useState(false);

  const subtasks = data?.tasks ?? [];
  const progress = data?.progress ?? { done: 0, total: 0 };
  const pct = progress.total > 0 ? Math.round((progress.done / progress.total) * 100) : 0;

  /**
   * Create one sub-issue from the quick-add box.
   *
   * The title is cleared BEFORE the mutation starts, and an in-flight create
   * short-circuits (BUG-4). Both matter because this box submits on Enter *and*
   * on blur: pressing Enter left the text in a still-focused input while the
   * request was in flight, so clicking away — or hitting Enter again on a slow
   * request — submitted the very same title a second and third time. Clearing
   * first makes the blur a no-op, and the pending guard covers the double Enter.
   */
  function add() {
    const t = title.trim();
    if (!t || create.isPending) {
      setAdding(false);
      return;
    }
    setTitle('');
    create.mutate(
      { title: t, parentId: taskId, projectId: parentProjectId },
      {
        onSuccess: () => setAdding(false),
        // Hand the text back so a failed create is not silently lost.
        onError: () => {
          setTitle(t);
          setAdding(true);
        },
      },
    );
  }

  return (
    <section className="mt-7">
      <div className="flex items-center justify-between">
        <h3 className="flex items-center gap-2 text-[13px] font-semibold text-gray-500">
          Sub-sarcini
          {progress.total > 0 && (
            <span className="text-[12px] font-normal text-gray-400">
              {progress.done}/{progress.total}
            </span>
          )}
        </h3>
        <button
          type="button"
          onClick={() => setAdding(true)}
          className="inline-flex items-center gap-1 rounded px-1.5 py-1 text-[12px] text-gray-500 hover:bg-gray-200 hover:text-gray-800"
        >
          <Plus size={13} /> Adaugă sub-sarcini
        </button>
      </div>

      {progress.total > 0 && (
        <div className="mt-2 h-1 w-full overflow-hidden rounded-full bg-gray-300">
          <div className="h-full rounded-full bg-green-500" style={{ width: `${pct}%` }} />
        </div>
      )}

      {isLoading && <p className="mt-2 text-[13px] text-gray-400">Se încarcă…</p>}

      <ul className="mt-2">
        {subtasks.map((st) => (
          <li key={st.id}>
            <button
              type="button"
              onClick={() => onOpenTask(st.identifier ?? st.id)}
              className="flex w-full items-center gap-2 rounded px-2 py-1.5 text-left text-[13px] transition-colors hover:bg-white/[0.03]"
            >
              <StatusIcon category={st.statusCategory} color={st.statusColor} size={14} />
              <span className="font-mono text-[11px] text-gray-400">{st.identifier ?? '—'}</span>
              <span className="truncate text-gray-700">{st.title}</span>
            </button>
          </li>
        ))}
      </ul>

      {adding && (
        <input
          autoFocus
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          onBlur={add}
          onKeyDown={(e) => {
            if (e.key === 'Enter') add();
            if (e.key === 'Escape') {
              setTitle('');
              setAdding(false);
            }
          }}
          placeholder="Titlu sub-sarcină…"
          className="mt-2 w-full rounded border border-gray-200 bg-transparent px-2 py-1.5 text-[13px] text-gray-800 placeholder:text-gray-400 focus:border-brand-600 focus:outline-none"
        />
      )}

      {!adding && subtasks.length === 0 && !isLoading && (
        <p className="mt-1 text-[13px] text-gray-400">Nicio sub-sarcină.</p>
      )}
    </section>
  );
}
